<template>
  <div class="dialog-launcher">
    <v-btn class="btn-ghost-light" @click="openDialog">
      <v-icon left>mdi-swap-horizontal</v-icon>Transfer
    </v-btn>
    <v-dialog v-model="dialog" persistent scrollable max-width="1024">
      <v-card>
        <div class="fdlg__form">
          <div class="fdlg__head">
            <div>
              <h2 class="fdlg__title">Transfer de echipamente</h2>
              <p class="fdlg__sub">Alegeți de la cine și la cine trec echipamentele. Câmpurile cu * sunt obligatorii.</p>
            </div>
            <v-btn icon aria-label="Închide" @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text ref="body" class="fdlg__body">
            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-account-switch-outline</v-icon>Persoanele</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="sourceUserId"
                    :items="users"
                    label="Utilizator sursă (de la cine)*"
                    item-text="user"
                    item-value="id"
                    prepend-inner-icon="mdi-account-arrow-right-outline"
                    outlined
                    clearable
                    @change="onSourceUserChange"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="destinationUserId"
                    :items="filteredDestinationUsers"
                    label="Utilizator destinație (la cine)*"
                    item-text="user"
                    item-value="id"
                    prepend-inner-icon="mdi-account-arrow-left-outline"
                    outlined
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-switch
                    v-model="deactivateSourceUser"
                    label="Dezactivează contul utilizatorului sursă"
                    inset
                    color="primary"
                    hide-details
                    class="mt-0 mb-3"
                  ></v-switch>
                </v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-package-variant-closed</v-icon>Echipamentele de transferat*</div>

              <p v-if="!sourceUserId" class="form-section__note xfer__hint">
                Alegeți utilizatorul sursă ca să vedeți echipamentele lui active.
              </p>

              <template v-if="sourceEquipments.length > 0">
                <div class="xfer__select-bar">
                  <v-checkbox
                    v-model="selectAll"
                    label="Selectează tot"
                    color="primary"
                    @change="toggleSelectAll"
                    hide-details
                    class="mt-0 pt-0"
                  ></v-checkbox>
                  <span class="xfer__count">
                    Selectate: <strong>{{ selectedEquipments.length }}</strong> din {{ sourceEquipments.length }}
                  </span>
                </div>
                <v-data-table
                  :headers="equipmentHeaders"
                  :items="sourceEquipments"
                  :items-per-page="-1"
                  hide-default-footer
                  class="xfer__table"
                  show-select
                  v-model="selectedEquipments"
                  item-key="id"
                >
                </v-data-table>
              </template>

              <div v-if="sourceUserId && sourceEquipments.length === 0 && !loadingEquipments" class="xfer__empty">
                <v-icon>mdi-information-outline</v-icon>
                Acest utilizator nu are echipamente active.
              </div>

              <div v-if="loadingEquipments" class="xfer__loading">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-map-marker-outline</v-icon>Locația și departamentul noi</div>
              <p class="form-section__note">
                Opțional. Dacă rămân goale, se păstrează cele actuale ale fiecărui echipament.
              </p>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="selectedBlock"
                    :items="blocks"
                    label="Bloc"
                    item-text="block"
                    item-value="block"
                    prepend-inner-icon="mdi-office-building-outline"
                    outlined
                    clearable
                    @change="onBlockChange"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="selectedFloor"
                    :items="floors"
                    label="Etaj"
                    item-text="floor"
                    item-value="floor"
                    prepend-inner-icon="mdi-stairs"
                    outlined
                    :disabled="!selectedBlock"
                    clearable
                    @change="onFloorChange"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="selectedRoom"
                    :items="rooms"
                    label="Cameră"
                    item-text="room"
                    item-value="room"
                    prepend-inner-icon="mdi-door"
                    outlined
                    :disabled="!selectedFloor"
                    clearable
                    :error-messages="locationError"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="selectedDepartmentId"
                    :items="departmentOptions"
                    label="Departament"
                    item-text="label"
                    item-value="id"
                    prepend-inner-icon="mdi-domain"
                    outlined
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-note-text-outline</v-icon>Notă</div>
              <v-textarea
                v-model="notes"
                label="Notă (opțional)"
                placeholder="De exemplu: motivul transferului"
                outlined
                rows="2"
                auto-grow
                counter="500"
                maxlength="500"
              ></v-textarea>
            </section>
          </v-card-text>

          <div v-if="successMessage || errorMessage" class="xfer__alerts">
            <v-alert v-if="successMessage" type="success" text dense dismissible class="mb-0" @input="successMessage = ''">
              {{ successMessage }}
            </v-alert>
            <v-alert v-if="errorMessage" type="error" text dense dismissible class="mb-0" @input="errorMessage = ''">
              {{ errorMessage }}
            </v-alert>
          </div>

          <div class="fdlg__foot">
            <v-spacer></v-spacer>
            <v-btn text large @click="closeDialog">Anulare</v-btn>
            <v-btn
              class="btn-green xfer__submit"
              large
              :disabled="!canTransfer"
              :loading="transferring"
              @click="doTransfer"
            >
              <v-icon left>mdi-swap-horizontal</v-icon>Transferă
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import { offeredLocations } from "./pickers";

export default {
  name: "TransferEquipmentDialog",
  data() {
    return {
      dialog: false,
      users: [],
      sourceUserId: null,
      destinationUserId: null,
      sourceEquipments: [],
      selectedEquipments: [],
      selectAll: false,
      deactivateSourceUser: false,
      blocks: [],
      floors: [],
      rooms: [],
      departments: [],
      selectedBlock: null,
      selectedFloor: null,
      selectedRoom: null,
      selectedDepartmentId: null,
      notes: "",
      loadingEquipments: false,
      transferring: false,
      successMessage: "",
      errorMessage: "",
      equipmentHeaders: [
        { text: "Tip", value: "equipmentType", sortable: false },
        { text: "Model", value: "model", sortable: false },
        { text: "Nr. Inventar", value: "registration_number", sortable: false },
        { text: "Locație", value: "locationInfo", sortable: false },
      ],
    };
  },
  computed: {
    // the source list keeps blocked accounts (equipment of someone who left), the destination doesn't
    filteredDestinationUsers() {
      return this.users.filter((u) => u.id !== this.sourceUserId && u.isActive !== false);
    },
    // Show the parent name so identically named sub-departments can't be confused.
    // Departments hidden in Configurare take no new equipment.
    departmentOptions() {
      const byId = new Map(this.departments.map((d) => [d.id, d]));
      return this.departments
        .filter((d) => !d.is_hidden)
        .map((d) => {
          const parent = byId.get(d.parent_id);
          return { id: d.id, label: parent ? `${parent.name} › ${d.name}` : d.name };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Location is all-or-nothing: either keep the current one or pick block + floor + room
    locationError() {
      return this.selectedBlock && !this.selectedRoom
        ? "Completați blocul, etajul și camera sau goliți câmpurile."
        : "";
    },
    canTransfer() {
      return (
        this.sourceUserId &&
        this.destinationUserId &&
        this.sourceUserId !== this.destinationUserId &&
        this.selectedEquipments.length > 0 &&
        !this.locationError &&
        !this.transferring
      );
    },
  },
  watch: {
    // "Selectează tot" follows ticks made row by row or with the table's own checkbox
    selectedEquipments(list) {
      this.selectAll = this.sourceEquipments.length > 0 && list.length === this.sourceEquipments.length;
    },
  },
  methods: {
    async openDialog() {
      this.dialog = true;
      helper.scrollToTop(this);
      await Promise.all([this.fetchUsers(), this.fetchLocationData()]);
    },
    async fetchLocationData() {
      try {
        const [blocks, departments] = await Promise.all([
          api.fetchBlocks(),
          api.getDepartments(),
        ]);
        this.blocks = helper.sortNatural(offeredLocations(blocks), "block");
        this.departments = departments;
      } catch (error) {
        console.error("Error fetching locations/departments:", error);
      }
    },
    async onBlockChange() {
      this.selectedFloor = null;
      this.selectedRoom = null;
      this.floors = [];
      this.rooms = [];
      if (!this.selectedBlock) return;
      try {
        this.floors = offeredLocations(await api.getFloorsByBlock(this.selectedBlock));
      } catch (error) {
        console.error("Error fetching floors:", error);
      }
    },
    async onFloorChange() {
      this.selectedRoom = null;
      this.rooms = [];
      if (!this.selectedFloor) return;
      try {
        this.rooms = offeredLocations(await api.getRoomsByBlockAndFloor(this.selectedBlock, this.selectedFloor));
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.sourceUserId = null;
      this.destinationUserId = null;
      this.sourceEquipments = [];
      this.selectedEquipments = [];
      this.selectAll = false;
      this.deactivateSourceUser = false;
      this.selectedBlock = null;
      this.selectedFloor = null;
      this.selectedRoom = null;
      this.floors = [];
      this.rooms = [];
      this.selectedDepartmentId = null;
      this.notes = "";
      this.successMessage = "";
      this.errorMessage = "";
    },
    async fetchUsers() {
      try {
        this.users = await api.getUsersDropDown();
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    async onSourceUserChange() {
      // the person picked first as destination cannot also be the source
      if (this.destinationUserId && this.destinationUserId === this.sourceUserId) {
        this.destinationUserId = null;
      }
      this.sourceEquipments = [];
      this.selectedEquipments = [];
      this.selectAll = false;

      if (!this.sourceUserId) return;

      this.loadingEquipments = true;
      try {
        const equipments = await api.getEquipmentsByUser(this.sourceUserId);
        this.sourceEquipments = equipments.map((eq) => ({
          id: eq.id,
          equipmentType: eq.equipmentTypeId?.type || "",
          model: eq.model,
          registration_number: eq.registration_number,
          locationInfo: eq.locationId
            ? `${eq.locationId.block || ""} ${eq.locationId.floor || ""} ${eq.locationId.room || ""}`.trim()
            : "",
        }));
      } catch (error) {
        console.error("Error fetching source equipment:", error);
        this.errorMessage = "Eroare la încărcarea echipamentelor.";
      } finally {
        this.loadingEquipments = false;
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedEquipments = [...this.sourceEquipments];
      } else {
        this.selectedEquipments = [];
      }
    },
    async doTransfer() {
      this.successMessage = "";
      this.errorMessage = "";
      this.transferring = true;

      try {
        const equipmentIds = this.selectedEquipments.map((eq) => eq.id);
        let newLocationId = null;
        if (this.selectedRoom) {
          newLocationId = await api.getLocationIdByDetails(
            this.selectedBlock,
            this.selectedFloor,
            this.selectedRoom
          );
          if (!newLocationId) {
            this.errorMessage = "Locația selectată nu a fost găsită.";
            return;
          }
        }

        await api.transferEquipments({
          equipmentIds,
          newResponsiblePersonId: this.destinationUserId,
          deactivateSourceUser: this.deactivateSourceUser,
          newLocationId,
          newDepartmentId: this.selectedDepartmentId,
          notes: this.notes,
        });
        this.successMessage = `${equipmentIds.length} echipament(e) transferat(e) cu succes.`;
        this.$emit("updateEquipmentData");

        // Reset everything except the success message after a successful transfer
        const message = this.successMessage;
        this.resetForm();
        this.successMessage = message;
      } catch (error) {
        console.error("Error transferring equipment:", error);
        this.errorMessage =
          error.response?.data?.message || "Eroare la transferul echipamentelor.";
      } finally {
        this.transferring = false;
      }
    },
  },
};
</script>

<style scoped>
.xfer__hint {
  margin: 0 0 14px;
}
.xfer__select-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 10px;
}
.xfer__count {
  color: var(--ink-2);
  font-size: 0.95rem;
}
.xfer__count strong {
  color: var(--green);
}
.xfer__table {
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
}
.xfer__empty {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  border: 1px dashed var(--line);
  border-radius: 10px;
  background: var(--mint-50);
  color: var(--ink-2);
}
.xfer__empty .v-icon {
  color: var(--green);
}
.xfer__loading {
  display: flex;
  justify-content: center;
  padding: 16px 0 22px;
}
.xfer__alerts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid var(--line-soft);
  background: var(--paper);
}
.v-btn.btn-green.xfer__submit.v-btn--disabled {
  background-color: var(--line-soft) !important;
  color: var(--ink-3) !important;
}
</style>
